import { useEffect, useState, type ReactNode } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import {
  gxPaymentsBulkTransactionDetailsApi,
  gxPayrollTransactionDetailsApi,
} from "@/app/apiConfig/services/bulkPaymentsPayrollApiLayer";
import type {
  PaymentsBulkTransactionDetailsResponse,
  PayrollTransactionDetailsResponse,
} from "@/app/types/bulkPaymentsPayroll";
import { gxCurrencyFormat } from "@/app/utils/helpers/gxCurrencyFormat";
import { gxDateTimeFormat } from "@/app/utils/helpers/gxDateTimeFormat";
import StatusBadge from "@/app/components/common/badge/StatusBadge";
import MaskedAccountNumber from "@/app/utils/helpers/MaskedAccountNumber";
import Bulkuploadviewskeleton from "../BulkPaymentPayrollReviewSkeleton/Bulkuploadviewskeleton";
import type { BulkUploadViewProps } from "../BulkUpload";
import { t } from "i18next";

const BulkUploadView = ({
  reviewSummary,
  payrollReviewSummary,
  isPayroll = false,
  transactionIdentifiers,
  usePayrollApi = false, // <- ADD: controls which API to call, independent of layout
}: BulkUploadViewProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailedPaymentData, setDetailedPaymentData] =
    useState<PaymentsBulkTransactionDetailsResponse | null>(null);
  const [detailedPayrollData, setDetailedPayrollData] =
    useState<PayrollTransactionDetailsResponse | null>(null);
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({
    transferFrom: true,
    transferTo: true,
    transactionDetails: true,
    paymentTimeline: true,
  });

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // ADD after the existing payrollData field accessors block,
  // before the paymentData field accessors block:

  // ==========================================
  // NON PAYROLL / NON WPS: these files use the payroll API
  // (so data lands in detailedPayrollData) but render the payment layout.
  // Map the payroll API response fields → payment field names.
  // The payroll transaction details API returns payment-style fields
  // (beneAccountName, debitAmount, beneAccountNo etc.) for NON PAYROLL files.
  // ==========================================
  const isNonPayrollLayout = usePayrollApi && !isPayroll;

  // When NON PAYROLL: read payment fields from detailedPayrollData (payroll API response)
  // When PAYMENT: read payment fields from detailedPaymentData (payment API response)
  const activePaymentData = isNonPayrollLayout
    ? (detailedPayrollData as any) // payroll API returns same payment fields for NON PAYROLL
    : detailedPaymentData;

  // ==========================================
  // transaction-details api
  // This is the ONLY source of truth for all UI fields below.
  // Do NOT fall back to reviewSummary / paymentData / payrollData
  // for transaction-specific fields - the review API returns nulls.
  // ==========================================
  useEffect(() => {
    const fetchTransactionDetails = async () => {
      if (!transactionIdentifiers) return;
      const { uniqueKey, statusId } = transactionIdentifiers;
      if (!uniqueKey || !statusId) return;

      setLoading(true);
      setError(null);
      setDetailedPaymentData(null);
      setDetailedPayrollData(null);

      try {
        // BEFORE:
        // if (isPayroll) {
        //   // === Pure Payroll ===
        //   const response = await gxPayrollTransactionDetailsApi({
        //     uniquekey: uniqueKey,
        //     statusId,
        //   });
        //   if (response.status === 1 && response.data) {
        //     setDetailedPayrollData(response.data);
        //   } else {
        //     setError("Failed to fetch payroll transaction details.");
        //   }
        // } else {
        //   // === Payment + NON PAYROLL ===
        //   const response = await gxPaymentsBulkTransactionDetailsApi({
        //     uniquekey: uniqueKey,
        //     statusId,
        //   });
        //   if (response.status === 1 && response.data) {
        //     setDetailedPaymentData(response.data);
        //   } else {
        //     setError("Failed to fetch payment transaction details.");
        //   }

        // AFTER:
        if (usePayrollApi) {
          // Payroll table API — used for: pure PAYROLL + NON PAYROLL + NON WPS
          const response = await gxPayrollTransactionDetailsApi({
            uniquekey: uniqueKey,
            statusId,
          });
          if (response.status === 1 && response.data) {
            setDetailedPayrollData(response.data);
          } else {
            setError("Failed to fetch payroll transaction details.");
          }
        } else {
          // Payment table API — used for: PAYMENT only
          const response = await gxPaymentsBulkTransactionDetailsApi({
            uniquekey: uniqueKey,
            statusId,
          });
          if (response.status === 1 && response.data) {
            setDetailedPaymentData(response.data);
          } else {
            setError("Failed to fetch payment transaction details.");
          }
        }
      } catch {
        setError("An error occurred while fetching transaction details.");
      } finally {
        setLoading(false);
      }
    };

    fetchTransactionDetails();
    // BEFORE:
    // }, [transactionIdentifiers, isPayroll]);

    // AFTER:
  }, [transactionIdentifiers, usePayrollApi]);

  if (loading) return <Bulkuploadviewskeleton />;

  if (error) {
    return (
      <div className="gx-bup-error-container">
        <p className="gx-bup-error-message gx-p14-b">{error}</p>
      </div>
    );
  }

  // ==========================================
  // CURRENCY HELPERS
  // ==========================================

  const formatAmount = (
    amount: number | string | null | undefined,
    currency: string | null | undefined,
  ): ReactNode => {
    const num = Number(amount);
    if (!amount || Number.isNaN(num)) return "--";
    return gxCurrencyFormat(num, currency || "EUR", undefined, {
      showSymbol: true,
      showCode: false,
    }).formatted;
  };

  // ==========================================
  // PAYROLL: field accessors - source: detailedPayrollData ONLY
  // ==========================================

  const payrollBeneName = detailedPayrollData?.employeeUniqueId || "--";

  const payrollHeaderAmount = (() => {
    const fixed = Number(detailedPayrollData?.fixedSalary || 0);
    const variable = Number(detailedPayrollData?.incVariableSalary || 0);
    const currency =
      payrollReviewSummary?.wpsBulkFileTransactionsHDREntity?.paymentCurrency ||
      detailedPayrollData?.debitCurrency;
    return formatAmount(fixed + variable, currency);
  })();

  const payrollDebitAccountName = detailedPayrollData?.debitAccountName || "--";
  const payrollAccountNickname = detailedPayrollData?.accountNickname || "--";
  const payrollTransferDate = (() => {
    if (!detailedPayrollData?.disbursementDate) return "--";
    return (
      gxDateTimeFormat(detailedPayrollData.disbursementDate).split(",")[0] ||
      "--"
    );
  })();

  const payrollRequestDateTime =
    gxDateTimeFormat(
      payrollReviewSummary?.wpsBulkFileTransactionsHDREntity?.crtdDt,
    ) || "--";

  const payrollEmpAccountNo = detailedPayrollData?.empAccountNo || null;
  const payrollAgentId = detailedPayrollData?.agentId || "--";

  const payrollFixedSalary = formatAmount(
    detailedPayrollData?.fixedSalary,
    detailedPayrollData?.debitCurrency,
  );
  const payrollVariableSalary = formatAmount(
    detailedPayrollData?.incVariableSalary,
    detailedPayrollData?.debitCurrency,
  );
  const payrollHousingAllowance = formatAmount(
    detailedPayrollData?.housingAllowance,
    detailedPayrollData?.debitCurrency,
  );
  const payrollConveyanceAllowance = formatAmount(
    detailedPayrollData?.conveyanceAllowance,
    detailedPayrollData?.debitCurrency,
  );
  const payrollMedicalAllowance = formatAmount(
    detailedPayrollData?.medicalAllowance,
    detailedPayrollData?.debitCurrency,
  );
  const payrollAnnualPassageAllowance = formatAmount(
    detailedPayrollData?.annualPassageAllowance,
    detailedPayrollData?.debitCurrency,
  );
  const payrollOvertimeAllowance = formatAmount(
    detailedPayrollData?.overtimeAllowance,
    detailedPayrollData?.debitCurrency,
  );
  const payrollOtherAllowance = formatAmount(
    detailedPayrollData?.otherAllowance,
    detailedPayrollData?.debitCurrency,
  );
  const payrollLeaveEncashment = formatAmount(
    detailedPayrollData?.leaveEncashment,
    detailedPayrollData?.debitCurrency,
  );
  const payrollRecordStatusCode = detailedPayrollData?.recordStatusCode || "";

  const payrollPayStartDate = detailedPayrollData?.payStartDate || "--";
  const payrollPayEndDate = detailedPayrollData?.payEndDate || "--";
  const payrollPayDuration = detailedPayrollData?.payDuration || "--";
  const payrollLeaveDays = detailedPayrollData?.leaveDays || "--";

  // ==========================================
  // PAYMENT / NON PAYROLL: field accessors - detailedPaymentData ONLY
  // ==========================================

  // const paymentBeneName = detailedPaymentData?.beneAccountName || "--";

  // const paymentHeaderAmount = formatAmount(
  //   detailedPaymentData?.debitAmount,
  //   detailedPaymentData?.debitCurrency,
  // );

  // const paymentDebitAccountName = detailedPaymentData?.debitAccountName || "--";
  // const paymentAccountNickname = detailedPaymentData?.accountNickname || "--";
  // const paymentTransferType = detailedPaymentData?.paymentTypeName || "--";
  // const paymentTransferDate = detailedPaymentData?.paymentDate || "--";
  // const paymentRequestDateTime =
  //   gxDateTimeFormat(reviewSummary?.crtdDt) || "--";

  // const paymentBeneAccountNo = detailedPaymentData?.beneAccountNo || null;
  // const paymentBeneAddress = detailedPaymentData?.beneAddressLine1 || "--";
  // const paymentBeneBankName = detailedPaymentData?.beneBankName || "--";
  // const paymentBeneBankAddress =
  //   detailedPaymentData?.beneBankAddressLine1 || "--";

  // const paymentAmount = formatAmount(
  //   detailedPaymentData?.debitAmount,
  //   detailedPaymentData?.debitCurrency,
  // );
  // const paymentForexRate =
  //   detailedPaymentData?.forexRate !== undefined &&
  //   detailedPaymentData?.forexRate !== null
  //     ? String(detailedPaymentData.forexRate)
  //     : "--";
  // const paymentCbsReferenceNo = detailedPaymentData?.cbsReferenceNo || "--";
  // const paymentPurposeDesc = detailedPaymentData?.purposeDesc || "--";
  // const paymentDetails = detailedPaymentData?.paymentDetails || "--";
  // const paymentReference = detailedPaymentData?.paymentReference || "--";
  // const paymentCategory = detailedPaymentData?.paymentCategory || "--";
  // const paymentChargeAmount = formatAmount(
  //   detailedPaymentData?.chargeAmount,
  //   detailedPaymentData?.chargeCurrency,
  // );
  // const paymentChargeBearerType = detailedPaymentData?.chargeBearerType || "--";
  // const paymentRecordStatusCode = detailedPaymentData?.recordStatusCode || "";

  // AFTER — replace detailedPaymentData with activePaymentData throughout:
  const paymentBeneName = activePaymentData?.beneAccountName || "--";
  const paymentHeaderAmount = formatAmount(
    activePaymentData?.debitAmount,
    activePaymentData?.debitCurrency,
  );
  const paymentDebitAccountName = activePaymentData?.debitAccountName || "--";
  const paymentAccountNickname = activePaymentData?.accountNickname || "--";
  const paymentTransferType = activePaymentData?.paymentTypeName || "--";
  const paymentTransferDate = activePaymentData?.paymentDate || "--";
  const paymentRequestDateTime =
    gxDateTimeFormat(
      isNonPayrollLayout
        ? (activePaymentData?.crtdDt ?? reviewSummary?.crtdDt) // NON PAYROLL: use crtdDt from API response
        : reviewSummary?.crtdDt,
    ) || "--";
  const paymentBeneAccountNo = activePaymentData?.beneAccountNo || null;
  const paymentBeneAddress = activePaymentData?.beneAddressLine1 || "--";
  const paymentBeneBankName = activePaymentData?.beneBankName || "--";
  const paymentBeneBankAddress =
    activePaymentData?.beneBankAddressLine1 || "--";
  const paymentAmount = formatAmount(
    activePaymentData?.debitAmount,
    activePaymentData?.debitCurrency,
  );
  const paymentForexRate =
    activePaymentData?.forexRate !== undefined &&
    activePaymentData?.forexRate !== null
      ? String(activePaymentData.forexRate)
      : "--";
  const paymentCbsReferenceNo = activePaymentData?.cbsReferenceNo || "--";
  const paymentPurposeDesc = activePaymentData?.purposeDesc || "--";
  const paymentDetails = activePaymentData?.paymentDetails || "--";
  const paymentReference = activePaymentData?.paymentReference || "--";
  const paymentCategory = activePaymentData?.paymentCategory || "--";
  const paymentChargeAmount = formatAmount(
    activePaymentData?.chargeAmount,
    activePaymentData?.chargeCurrency,
  );
  const paymentChargeBearerType = activePaymentData?.chargeBearerType || "--";
  const paymentRecordStatusCode = activePaymentData?.recordStatusCode || "";

  const renderTransferToAccountNumber = () => {
    if (isPayroll) {
      if (payrollEmpAccountNo) {
        return (
          <MaskedAccountNumber
            accountNumber={payrollEmpAccountNo}
            showToggle
            toggleIconClassName="w-3 h-3 cursor-pointer opacity-70 hover:opacity-100"
          />
        );
      }
      return (
        <span className="gx-bpr-payroll-pd-in-cnt-red">{t("missing")}</span>
      );
    }
    if (paymentBeneAccountNo) {
      return (
        <MaskedAccountNumber accountNumber={paymentBeneAccountNo} showToggle />
      );
    }
    return "--";
  };

  // ==========================================
  // RENDER
  // ==========================================
  return (
    <div className="gx-bpr-sb-sep-one-detail-sec">
      {/* === Header === */}
      <div className="gx-bpr-sb-sep-one-detail-hd">
        <h2 className="gx-h2-24-l">
          {t("to")}{" "}
          <span className="gx-h2-24-b">
            {isPayroll ? payrollBeneName : paymentBeneName}
          </span>
        </h2>
      </div>

      {/* === Amount Card === */}
      <div className="gx-bpr-sb-sep-one-detail-a-c-sec">
        <div className="gx-bpr-sb-sep-one-detail-a-c-in-fl">
          <span className="gx-bpr-sb-sep-one-detail-amt">
            <p className="gx-p14-md">{t("payment_amount")}</p>
          </span>
          <span className="gx-bpr-pr-3">
            <p className="gx-h3-sb">
              {isPayroll ? payrollHeaderAmount : paymentHeaderAmount}
            </p>
          </span>
        </div>
      </div>

      {/* === Transfer From === */}
      <div className="gx-bpr-sb-sep-one-trans-from-sec">
        <button
          type="button"
          className="gx-bpr-sb-sep-one-trans-from-fl"
          onClick={() => toggleSection("transferFrom")}
        >
          <h3 className="gx-p18-b">{t("transfer_from")}</h3>
          {expandedSections.transferFrom ? (
            <ChevronUp size={20} className="gx-bpr-sb-sep-one-trans-from-d-a" />
          ) : (
            <ChevronDown
              size={20}
              className="gx-bpr-sb-sep-one-trans-from-d-a"
            />
          )}
        </button>

        {expandedSections.transferFrom && (
          <div className="gx-bpr-sb-sep-one-trans-from-in-data-sec">
            <div className="gx-bpr-sb-sep-one-trans-from-in-d-sec-fl">
              <span className="gx-bpr-sb-sep-one-trans-from-in-d-l">
                <p className="gx-p12-m">{t("account_name")}</p>
              </span>
              <span className="gx-bpr-sb-sep-one-trans-from-in-d-r">
                <p className="gx-p12-b">
                  {isPayroll
                    ? payrollDebitAccountName
                    : paymentDebitAccountName}
                </p>
              </span>
            </div>

            <div className="gx-bpr-sb-sep-one-trans-from-in-d-sec-fl">
              <span className="gx-bpr-sb-sep-one-trans-from-in-d-l">
                <p className="gx-p12-m">{t("account_nick_name")}</p>
              </span>
              <span className="gx-bpr-sb-sep-one-trans-from-in-d-r">
                <p className="gx-p12-b">
                  {isPayroll ? payrollAccountNickname : paymentAccountNickname}
                </p>
              </span>
            </div>

            {/* Transfer Type - Payment only */}
            {!isPayroll && (
              <div className="gx-bpr-sb-sep-one-trans-from-in-d-sec-fl">
                <span className="gx-bpr-sb-sep-one-trans-from-in-d-l">
                  <p className="gx-p12-m">{t("transfer_type")}</p>
                </span>
                <span className="gx-bpr-sb-sep-one-trans-from-in-d-r">
                  <p className="gx-p12-b">{paymentTransferType}</p>
                </span>
              </div>
            )}

            <div className="gx-bpr-sb-sep-one-trans-from-in-d-sec-fl">
              <span className="gx-bpr-sb-sep-one-trans-from-in-d-l">
                <p className="gx-p12-m">{t("transfer_date")}</p>
              </span>
              <span className="gx-bpr-sb-sep-one-trans-from-in-d-r">
                <p className="gx-p12-b">
                  {isPayroll ? payrollTransferDate : paymentTransferDate}
                </p>
              </span>
            </div>

            <div className="gx-bpr-sb-sep-one-trans-from-in-d-sec-fl">
              <span className="gx-bpr-sb-sep-one-trans-from-in-d-l">
                <p className="gx-p12-m">{t("request_date_time")}</p>
              </span>
              <span className="gx-bpr-sb-sep-one-trans-from-in-d-r">
                <p className="gx-p12-b">
                  {isPayroll ? payrollRequestDateTime : paymentRequestDateTime}
                </p>
              </span>
            </div>
          </div>
        )}
      </div>

      {/* === Transfer To === */}
      <div className="gx-bpr-sb-sep-one-trans-s-sec">
        <button
          type="button"
          className="gx-bpr-sb-sep-one-trans-s-sec-fl"
          onClick={() => toggleSection("transferTo")}
        >
          <h3 className="gx-p18-b">{t("transfer_to")}</h3>
          {expandedSections.transferTo ? (
            <ChevronUp size={20} className="gx-bpr-sb-sep-one-trans-s-dd" />
          ) : (
            <ChevronDown size={20} className="gx-bpr-sb-sep-one-trans-s-dd" />
          )}
        </button>

        {expandedSections.transferTo && (
          <div className="gx-bpr-sb-sep-one-trans-s-in-data-sec">
            <div className="gx-bpr-sb-sep-one-trans-s-in-d-sec-fl">
              <span className="gx-bpr-sb-sep-one-trans-s-in-d-l">
                <p className="gx-p12-m">{t("account_number_iban")}</p>
              </span>
              <span className="gx-bpr-sb-sep-one-trans-s-in-d-r">
                <p className="gx-p12-b">{renderTransferToAccountNumber()}</p>
              </span>
            </div>

            {/* Payment + NON PAYROLL: beneficiary fields */}
            {!isPayroll && (
              <>
                <div className="gx-bpr-sb-sep-one-trans-s-in-d-sec-fl">
                  <span className="gx-bpr-sb-sep-one-trans-s-in-d-l">
                    <p className="gx-p12-m">{t("beneficiary_name")}</p>
                  </span>
                  <span className="gx-bpr-sb-sep-one-trans-s-in-d-r">
                    <p className="gx-p12-b">{paymentBeneName}</p>
                  </span>
                </div>

                <div className="gx-bpr-sb-sep-one-trans-s-in-d-sec-fl">
                  <span className="gx-bpr-sb-sep-one-trans-s-in-d-l">
                    <p className="gx-p12-m">{t("bene_address")}</p>
                  </span>
                  <span className="gx-bpr-sb-sep-one-trans-cnt-rt gx-bpr-sb-sep-one-trans-s-in-d-r">
                    <p className="gx-p12-b">{paymentBeneAddress}</p>
                  </span>
                </div>

                <div className="gx-bpr-sb-sep-one-trans-s-in-d-sec-fl">
                  <span className="gx-bpr-sb-sep-one-trans-s-in-d-l">
                    <p className="gx-p12-m">{t("beneficiary_bank_name")}</p>
                  </span>
                  <span className="gx-bpr-sb-sep-one-trans-s-in-d-r">
                    <p className="gx-p12-b">{paymentBeneBankName}</p>
                  </span>
                </div>

                <div className="gx-bpr-sb-sep-one-trans-s-in-d-sec-fl">
                  <span className="gx-bpr-sb-sep-one-trans-s-in-d-l">
                    <p className="gx-p12-m">{t("beneficiary_bank_address")}</p>
                  </span>
                  <span className="gx-bpr-sb-sep-one-trans-cnt-rt gx-bpr-sb-sep-one-trans-s-in-d-r">
                    <p className="gx-p12-b">{paymentBeneBankAddress}</p>
                  </span>
                </div>
              </>
            )}

            {isPayroll && (
              <div className="gx-bpr-sb-sep-one-trans-s-in-d-sec-fl">
                <span className="gx-bpr-sb-sep-one-trans-s-in-d-l">
                  <p className="gx-p12-m">{t("agent_code")}</p>
                </span>
                <span className="gx-bpr-sb-sep-one-trans-s-in-d-r">
                  <p className="gx-p12-b">{payrollAgentId}</p>
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* === Transaction Details / Payroll Summary === */}
      <div className="gx-bpr-sb-sep-one-trans-det-sec">
        <button
          type="button"
          className="gx-bpr-sb-sep-one-trans-det-sec-fl"
          onClick={() => toggleSection("transactionDetails")}
        >
          <h3 className="gx-p18-b">
            {isPayroll ? "Payroll Summary" : "Transaction Details"}
          </h3>
          {expandedSections.transactionDetails ? (
            <ChevronUp size={20} className="gx-bpr-sb-sep-one-trans-det-dd" />
          ) : (
            <ChevronDown size={20} className="gx-bpr-sb-sep-one-trans-det-dd" />
          )}
        </button>

        {expandedSections.transactionDetails && (
          <div className="gx-bpr-sb-sep-one-trans-det-in-data-sec">
            {/* === Payment + NON PAYROLL: Transaction Details === */}
            {!isPayroll && (
              <>
                <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
                  <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
                    <p className="gx-p12-m">{t("payment_amount")}</p>
                  </span>
                  <span>
                    <p className="gx-p12-b">{paymentAmount}</p>
                  </span>
                </div>

                <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
                  <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
                    <p className="gx-p12-m">{t("fx_rate_indicative")}</p>
                  </span>
                  <span>
                    <p className="gx-p12-b">{paymentForexRate}</p>
                  </span>
                </div>

                <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
                  <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
                    <p className="gx-p12-m">{t("upi_transaction_id")}</p>
                  </span>
                  <span>
                    <p className="gx-p12-b">{paymentCbsReferenceNo}</p>
                  </span>
                </div>

                <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
                  <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
                    <p className="gx-p12-m">{t("purpose_of_payment")}</p>
                  </span>
                  <span>
                    <p className="gx-p12-b">{paymentPurposeDesc}</p>
                  </span>
                </div>

                <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
                  <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
                    <p className="gx-p12-m">{t("payment_details")}</p>
                  </span>
                  <span>
                    <p className="gx-p12-b">{paymentDetails}</p>
                  </span>
                </div>

                <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
                  <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
                    <p className="gx-p12-m">{t("payment_reference")}</p>
                  </span>
                  <span>
                    <p className="gx-p12-b">{paymentReference}</p>
                  </span>
                </div>

                <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
                  <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
                    <p className="gx-p12-m">{t("payment_category")}</p>
                  </span>
                  <span>
                    <p className="gx-p12-b">{paymentCategory}</p>
                  </span>
                </div>

                <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
                  <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
                    <p className="gx-p12-m">{t("charge_amount")}</p>
                  </span>
                  <span>
                    <p className="gx-p12-b">{paymentChargeAmount}</p>
                  </span>
                </div>

                <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
                  <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
                    <p className="gx-p12-m">{t("who_pays_charges")}</p>
                  </span>
                  <span>
                    <p className="gx-p12-b">{paymentChargeBearerType}</p>
                  </span>
                </div>

                <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
                  <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
                    <p className="gx-p12-m">{t("status")}</p>
                  </span>
                  <span>
                    <StatusBadge
                      module="bulk"
                      statusCode={paymentRecordStatusCode}
                    />
                  </span>
                </div>
              </>
            )}

            {isPayroll && (
              <>
                <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
                  <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
                    <p className="gx-p12-m">{t("fixed_pay")}</p>
                  </span>
                  <span>
                    <p className="gx-p12-b">{payrollFixedSalary}</p>
                  </span>
                </div>

                <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
                  <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
                    <p className="gx-p12-m">{t("variable_pay")}</p>
                  </span>
                  <span>
                    <p className="gx-p12-b">{payrollVariableSalary}</p>
                  </span>
                </div>

                <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
                  <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
                    <p className="gx-p12-m">{t("housing_allowance")}</p>
                  </span>
                  <span>
                    <p className="gx-p12-b">{payrollHousingAllowance}</p>
                  </span>
                </div>

                <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
                  <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
                    <p className="gx-p12-m">{t("conveyance_allowance")}</p>
                  </span>
                  <span>
                    <p className="gx-p12-b">{payrollConveyanceAllowance}</p>
                  </span>
                </div>

                <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
                  <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
                    <p className="gx-p12-m">{t("medical_allowance")}</p>
                  </span>
                  <span>
                    <p className="gx-p12-b">{payrollMedicalAllowance}</p>
                  </span>
                </div>

                <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
                  <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
                    <p className="gx-p12-m">{t("annual_passage_allowance")}</p>
                  </span>
                  <span>
                    <p className="gx-p12-b">{payrollAnnualPassageAllowance}</p>
                  </span>
                </div>

                <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
                  <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
                    <p className="gx-p12-m">{t("overtime_allowance")}</p>
                  </span>
                  <span>
                    <p className="gx-p12-b">{payrollOvertimeAllowance}</p>
                  </span>
                </div>

                <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
                  <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
                    <p className="gx-p12-m">{t("all_other_allowances")}</p>
                  </span>
                  <span>
                    <p className="gx-p12-b">{payrollOtherAllowance}</p>
                  </span>
                </div>

                <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
                  <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
                    <p className="gx-p12-m">{t("leave_encashment")}</p>
                  </span>
                  <span>
                    <p className="gx-p12-b">{payrollLeaveEncashment}</p>
                  </span>
                </div>

                <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
                  <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
                    <p className="gx-p12-m">{t("status")}</p>
                  </span>
                  <span>
                    <StatusBadge
                      module="bulk"
                      statusCode={payrollRecordStatusCode}
                    />
                  </span>
                </div>

                <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl gx-bup-payment-details">
                  <h3 className="gx-p12-b">{t("payment_details")}</h3>
                </div>

                <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
                  <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
                    <p className="gx-p12-m">{t("bene_account_number_iban")}</p>
                  </span>
                  <span>
                    <p className="gx-p12-b">
                      {payrollEmpAccountNo ? (
                        <MaskedAccountNumber
                          accountNumber={payrollEmpAccountNo}
                          showToggle
                          toggleIconClassName="w-3 h-3 cursor-pointer opacity-70 hover:opacity-100"
                        />
                      ) : (
                        <span className="gx-bpr-payroll-pd-in-cnt-red">
                          {t("missing")}
                        </span>
                      )}
                    </p>
                  </span>
                </div>

                <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
                  <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
                    <p className="gx-p12-m">{t("agent_code")}</p>
                  </span>
                  <span>
                    <p className="gx-p12-b">{payrollAgentId}</p>
                  </span>
                </div>

                <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl gx-bup-payment-details">
                  <h3 className="gx-p12-b">{t("duration_and_attendance")}</h3>
                </div>

                <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
                  <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
                    <p className="gx-p12-m">{t("start")}</p>
                  </span>
                  <span>
                    <p className="gx-p12-b">{payrollPayStartDate}</p>
                  </span>
                </div>

                <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
                  <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
                    <p className="gx-p12-m">{t("end")}</p>
                  </span>
                  <span>
                    <p className="gx-p12-b">{payrollPayEndDate}</p>
                  </span>
                </div>

                <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
                  <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
                    <p className="gx-p12-m">{t("days_worked")}</p>
                  </span>
                  <span>
                    <p className="gx-p12-b">{payrollPayDuration}</p>
                  </span>
                </div>

                <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
                  <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
                    <p className="gx-p12-m">{t("leave")}</p>
                  </span>
                  <span>
                    <p className="gx-p12-b">{payrollLeaveDays}</p>
                  </span>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkUploadView;
// import { useEffect, useState, type ReactNode } from "react";
// import { ChevronDown, ChevronUp } from "lucide-react";
// import {
//   gxPaymentsBulkTransactionDetailsApi,
//   gxPayrollTransactionDetailsApi,
// } from "@/app/apiConfig/services/bulkPaymentsPayrollApiLayer";
// import type {
//   PaymentsBulkTransactionDetailsResponse,
//   PayrollTransactionDetailsResponse,
// } from "@/app/types/bulkPaymentsPayroll";
// import { gxCurrencyFormat } from "@/app/utils/helpers/gxCurrencyFormat";
// import { gxDateTimeFormat } from "@/app/utils/helpers/gxDateTimeFormat";
// import StatusBadge from "@/app/components/common/badge/StatusBadge";
// import MaskedAccountNumber from "@/app/utils/helpers/MaskedAccountNumber";
// import Bulkuploadviewskeleton from "../BulkPaymentPayrollReviewSkeleton/Bulkuploadviewskeleton";
// import type { BulkUploadViewProps } from "../BulkUpload";
// import { t } from "i18next";

// const BulkUploadView = ({
//   reviewSummary,
//   payrollReviewSummary,
//   isPayroll = false,
//   transactionIdentifiers,
//   usePayrollApi = false, // <- ADD: controls which API to call, independent of layout
// }: BulkUploadViewProps) => {
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState<string | null>(null);
//   const [detailedPaymentData, setDetailedPaymentData] =
//     useState<PaymentsBulkTransactionDetailsResponse | null>(null);
//   const [detailedPayrollData, setDetailedPayrollData] =
//     useState<PayrollTransactionDetailsResponse | null>(null);
//   const [expandedSections, setExpandedSections] = useState<
//     Record<string, boolean>
//   >({
//     transferFrom: true,
//     transferTo: true,
//     transactionDetails: true,
//     paymentTimeline: true,
//   });

//   const toggleSection = (section: string) => {
//     setExpandedSections((prev) => ({
//       ...prev,
//       [section]: !prev[section],
//     }));
//   };

//   // ==========================================
//   // transaction-details api
//   // This is the ONLY source of truth for all UI fields below.
//   // Do NOT fall back to reviewSummary / paymentData / payrollData
//   // for transaction-specific fields - the review API returns nulls.
//   // ==========================================
//   useEffect(() => {
//     const fetchTransactionDetails = async () => {
//       if (!transactionIdentifiers) return;
//       const { uniqueKey, statusId } = transactionIdentifiers;
//       if (!uniqueKey || !statusId) return;

//       setLoading(true);
//       setError(null);
//       setDetailedPaymentData(null);
//       setDetailedPayrollData(null);

//       try {
//         // BEFORE:
//         // if (isPayroll) {
//         //   // === Pure Payroll ===
//         //   const response = await gxPayrollTransactionDetailsApi({
//         //     uniquekey: uniqueKey,
//         //     statusId,
//         //   });
//         //   if (response.status === 1 && response.data) {
//         //     setDetailedPayrollData(response.data);
//         //   } else {
//         //     setError("Failed to fetch payroll transaction details.");
//         //   }
//         // } else {
//         //   // === Payment + NON PAYROLL ===
//         //   const response = await gxPaymentsBulkTransactionDetailsApi({
//         //     uniquekey: uniqueKey,
//         //     statusId,
//         //   });
//         //   if (response.status === 1 && response.data) {
//         //     setDetailedPaymentData(response.data);
//         //   } else {
//         //     setError("Failed to fetch payment transaction details.");
//         //   }

//         // AFTER:
//         if (usePayrollApi) {
//           // Payroll table API — used for: pure PAYROLL + NON PAYROLL + NON WPS
//           const response = await gxPayrollTransactionDetailsApi({
//             uniquekey: uniqueKey,
//             statusId,
//           });
//           if (response.status === 1 && response.data) {
//             setDetailedPayrollData(response.data);
//           } else {
//             setError("Failed to fetch payroll transaction details.");
//           }
//         } else {
//           // Payment table API — used for: PAYMENT only
//           const response = await gxPaymentsBulkTransactionDetailsApi({
//             uniquekey: uniqueKey,
//             statusId,
//           });
//           if (response.status === 1 && response.data) {
//             setDetailedPaymentData(response.data);
//           } else {
//             setError("Failed to fetch payment transaction details.");
//           }
//         }
//       } catch {
//         setError("An error occurred while fetching transaction details.");
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchTransactionDetails();
//     // BEFORE:
//     // }, [transactionIdentifiers, isPayroll]);

//     // AFTER:
//   }, [transactionIdentifiers, usePayrollApi]);

//   if (loading) return <Bulkuploadviewskeleton />;

//   if (error) {
//     return (
//       <div className="gx-bup-error-container">
//         <p className="gx-bup-error-message gx-p14-b">{error}</p>
//       </div>
//     );
//   }

//   // ==========================================
//   // CURRENCY HELPERS
//   // ==========================================

//   const formatAmount = (
//     amount: number | string | null | undefined,
//     currency: string | null | undefined,
//   ): ReactNode => {
//     const num = Number(amount);
//     if (!amount || Number.isNaN(num)) return "--";
//     return gxCurrencyFormat(num, currency || "EUR", undefined, {
//       showSymbol: true,
//       showCode: false,
//     }).formatted;
//   };

//   // ==========================================
//   // PAYROLL: field accessors - source: detailedPayrollData ONLY
//   // ==========================================

//   const payrollBeneName = detailedPayrollData?.employeeUniqueId || "--";

//   const payrollHeaderAmount = (() => {
//     const fixed = Number(detailedPayrollData?.fixedSalary || 0);
//     const variable = Number(detailedPayrollData?.incVariableSalary || 0);
//     const currency =
//       payrollReviewSummary?.wpsBulkFileTransactionsHDREntity?.paymentCurrency ||
//       detailedPayrollData?.debitCurrency;
//     return formatAmount(fixed + variable, currency);
//   })();

//   const payrollDebitAccountName = detailedPayrollData?.debitAccountName || "--";
//   const payrollAccountNickname = detailedPayrollData?.accountNickname || "--";
//   const payrollTransferDate = (() => {
//     if (!detailedPayrollData?.disbursementDate) return "--";
//     return (
//       gxDateTimeFormat(detailedPayrollData.disbursementDate).split(",")[0] ||
//       "--"
//     );
//   })();

//   const payrollRequestDateTime =
//     gxDateTimeFormat(
//       payrollReviewSummary?.wpsBulkFileTransactionsHDREntity?.crtdDt,
//     ) || "--";

//   const payrollEmpAccountNo = detailedPayrollData?.empAccountNo || null;
//   const payrollAgentId = detailedPayrollData?.agentId || "--";

//   const payrollFixedSalary = formatAmount(
//     detailedPayrollData?.fixedSalary,
//     detailedPayrollData?.debitCurrency,
//   );
//   const payrollVariableSalary = formatAmount(
//     detailedPayrollData?.incVariableSalary,
//     detailedPayrollData?.debitCurrency,
//   );
//   const payrollHousingAllowance = formatAmount(
//     detailedPayrollData?.housingAllowance,
//     detailedPayrollData?.debitCurrency,
//   );
//   const payrollConveyanceAllowance = formatAmount(
//     detailedPayrollData?.conveyanceAllowance,
//     detailedPayrollData?.debitCurrency,
//   );
//   const payrollMedicalAllowance = formatAmount(
//     detailedPayrollData?.medicalAllowance,
//     detailedPayrollData?.debitCurrency,
//   );
//   const payrollAnnualPassageAllowance = formatAmount(
//     detailedPayrollData?.annualPassageAllowance,
//     detailedPayrollData?.debitCurrency,
//   );
//   const payrollOvertimeAllowance = formatAmount(
//     detailedPayrollData?.overtimeAllowance,
//     detailedPayrollData?.debitCurrency,
//   );
//   const payrollOtherAllowance = formatAmount(
//     detailedPayrollData?.otherAllowance,
//     detailedPayrollData?.debitCurrency,
//   );
//   const payrollLeaveEncashment = formatAmount(
//     detailedPayrollData?.leaveEncashment,
//     detailedPayrollData?.debitCurrency,
//   );
//   const payrollRecordStatusCode = detailedPayrollData?.recordStatusCode || "";

//   const payrollPayStartDate = detailedPayrollData?.payStartDate || "--";
//   const payrollPayEndDate = detailedPayrollData?.payEndDate || "--";
//   const payrollPayDuration = detailedPayrollData?.payDuration || "--";
//   const payrollLeaveDays = detailedPayrollData?.leaveDays || "--";

//   // ==========================================
//   // PAYMENT / NON PAYROLL: field accessors - detailedPaymentData ONLY
//   // ==========================================

//   const paymentBeneName = detailedPaymentData?.beneAccountName || "--";

//   const paymentHeaderAmount = formatAmount(
//     detailedPaymentData?.debitAmount,
//     detailedPaymentData?.debitCurrency,
//   );

//   const paymentDebitAccountName = detailedPaymentData?.debitAccountName || "--";
//   const paymentAccountNickname = detailedPaymentData?.accountNickname || "--";
//   const paymentTransferType = detailedPaymentData?.paymentTypeName || "--";
//   const paymentTransferDate = detailedPaymentData?.paymentDate || "--";
//   const paymentRequestDateTime =
//     gxDateTimeFormat(reviewSummary?.crtdDt) || "--";

//   const paymentBeneAccountNo = detailedPaymentData?.beneAccountNo || null;
//   const paymentBeneAddress = detailedPaymentData?.beneAddressLine1 || "--";
//   const paymentBeneBankName = detailedPaymentData?.beneBankName || "--";
//   const paymentBeneBankAddress =
//     detailedPaymentData?.beneBankAddressLine1 || "--";

//   const paymentAmount = formatAmount(
//     detailedPaymentData?.debitAmount,
//     detailedPaymentData?.debitCurrency,
//   );
//   const paymentForexRate =
//     detailedPaymentData?.forexRate !== undefined &&
//     detailedPaymentData?.forexRate !== null
//       ? String(detailedPaymentData.forexRate)
//       : "--";
//   const paymentCbsReferenceNo = detailedPaymentData?.cbsReferenceNo || "--";
//   const paymentPurposeDesc = detailedPaymentData?.purposeDesc || "--";
//   const paymentDetails = detailedPaymentData?.paymentDetails || "--";
//   const paymentReference = detailedPaymentData?.paymentReference || "--";
//   const paymentCategory = detailedPaymentData?.paymentCategory || "--";
//   const paymentChargeAmount = formatAmount(
//     detailedPaymentData?.chargeAmount,
//     detailedPaymentData?.chargeCurrency,
//   );
//   const paymentChargeBearerType = detailedPaymentData?.chargeBearerType || "--";
//   const paymentRecordStatusCode = detailedPaymentData?.recordStatusCode || "";

//   const renderTransferToAccountNumber = () => {
//     if (isPayroll) {
//       if (payrollEmpAccountNo) {
//         return (
//           <MaskedAccountNumber
//             accountNumber={payrollEmpAccountNo}
//             showToggle
//             toggleIconClassName="w-3 h-3 cursor-pointer opacity-70 hover:opacity-100"
//           />
//         );
//       }
//       return (
//         <span className="gx-bpr-payroll-pd-in-cnt-red">{t("missing")}</span>
//       );
//     }
//     if (paymentBeneAccountNo) {
//       return (
//         <MaskedAccountNumber accountNumber={paymentBeneAccountNo} showToggle />
//       );
//     }
//     return "--";
//   };

//   // ==========================================
//   // RENDER
//   // ==========================================
//   return (
//     <div className="gx-bpr-sb-sep-one-detail-sec">
//       {/* === Header === */}
//       <div className="gx-bpr-sb-sep-one-detail-hd">
//         <h2 className="gx-h2-24-l">
//           {t("to")}{" "}
//           <span className="gx-h2-24-b">
//             {isPayroll ? payrollBeneName : paymentBeneName}
//           </span>
//         </h2>
//       </div>

//       {/* === Amount Card === */}
//       <div className="gx-bpr-sb-sep-one-detail-a-c-sec">
//         <div className="gx-bpr-sb-sep-one-detail-a-c-in-fl">
//           <span className="gx-bpr-sb-sep-one-detail-amt">
//             <p className="gx-p14-md">{t("payment_amount")}</p>
//           </span>
//           <span className="gx-bpr-pr-3">
//             <p className="gx-h3-sb">
//               {isPayroll ? payrollHeaderAmount : paymentHeaderAmount}
//             </p>
//           </span>
//         </div>
//       </div>

//       {/* === Transfer From === */}
//       <div className="gx-bpr-sb-sep-one-trans-from-sec">
//         <button
//           type="button"
//           className="gx-bpr-sb-sep-one-trans-from-fl"
//           onClick={() => toggleSection("transferFrom")}
//         >
//           <h3 className="gx-p18-b">{t("transfer_from")}</h3>
//           {expandedSections.transferFrom ? (
//             <ChevronUp size={20} className="gx-bpr-sb-sep-one-trans-from-d-a" />
//           ) : (
//             <ChevronDown
//               size={20}
//               className="gx-bpr-sb-sep-one-trans-from-d-a"
//             />
//           )}
//         </button>

//         {expandedSections.transferFrom && (
//           <div className="gx-bpr-sb-sep-one-trans-from-in-data-sec">
//             <div className="gx-bpr-sb-sep-one-trans-from-in-d-sec-fl">
//               <span className="gx-bpr-sb-sep-one-trans-from-in-d-l">
//                 <p className="gx-p12-m">{t("account_name")}</p>
//               </span>
//               <span className="gx-bpr-sb-sep-one-trans-from-in-d-r">
//                 <p className="gx-p12-b">
//                   {isPayroll
//                     ? payrollDebitAccountName
//                     : paymentDebitAccountName}
//                 </p>
//               </span>
//             </div>

//             <div className="gx-bpr-sb-sep-one-trans-from-in-d-sec-fl">
//               <span className="gx-bpr-sb-sep-one-trans-from-in-d-l">
//                 <p className="gx-p12-m">{t("account_nick_name")}</p>
//               </span>
//               <span className="gx-bpr-sb-sep-one-trans-from-in-d-r">
//                 <p className="gx-p12-b">
//                   {isPayroll ? payrollAccountNickname : paymentAccountNickname}
//                 </p>
//               </span>
//             </div>

//             {/* Transfer Type - Payment only */}
//             {!isPayroll && (
//               <div className="gx-bpr-sb-sep-one-trans-from-in-d-sec-fl">
//                 <span className="gx-bpr-sb-sep-one-trans-from-in-d-l">
//                   <p className="gx-p12-m">{t("transfer_type")}</p>
//                 </span>
//                 <span className="gx-bpr-sb-sep-one-trans-from-in-d-r">
//                   <p className="gx-p12-b">{paymentTransferType}</p>
//                 </span>
//               </div>
//             )}

//             <div className="gx-bpr-sb-sep-one-trans-from-in-d-sec-fl">
//               <span className="gx-bpr-sb-sep-one-trans-from-in-d-l">
//                 <p className="gx-p12-m">{t("transfer_date")}</p>
//               </span>
//               <span className="gx-bpr-sb-sep-one-trans-from-in-d-r">
//                 <p className="gx-p12-b">
//                   {isPayroll ? payrollTransferDate : paymentTransferDate}
//                 </p>
//               </span>
//             </div>

//             <div className="gx-bpr-sb-sep-one-trans-from-in-d-sec-fl">
//               <span className="gx-bpr-sb-sep-one-trans-from-in-d-l">
//                 <p className="gx-p12-m">{t("request_date_time")}</p>
//               </span>
//               <span className="gx-bpr-sb-sep-one-trans-from-in-d-r">
//                 <p className="gx-p12-b">
//                   {isPayroll ? payrollRequestDateTime : paymentRequestDateTime}
//                 </p>
//               </span>
//             </div>
//           </div>
//         )}
//       </div>

//       {/* === Transfer To === */}
//       <div className="gx-bpr-sb-sep-one-trans-s-sec">
//         <button
//           type="button"
//           className="gx-bpr-sb-sep-one-trans-s-sec-fl"
//           onClick={() => toggleSection("transferTo")}
//         >
//           <h3 className="gx-p18-b">{t("transfer_to")}</h3>
//           {expandedSections.transferTo ? (
//             <ChevronUp size={20} className="gx-bpr-sb-sep-one-trans-s-dd" />
//           ) : (
//             <ChevronDown size={20} className="gx-bpr-sb-sep-one-trans-s-dd" />
//           )}
//         </button>

//         {expandedSections.transferTo && (
//           <div className="gx-bpr-sb-sep-one-trans-s-in-data-sec">
//             <div className="gx-bpr-sb-sep-one-trans-s-in-d-sec-fl">
//               <span className="gx-bpr-sb-sep-one-trans-s-in-d-l">
//                 <p className="gx-p12-m">{t("account_number_iban")}</p>
//               </span>
//               <span className="gx-bpr-sb-sep-one-trans-s-in-d-r">
//                 <p className="gx-p12-b">{renderTransferToAccountNumber()}</p>
//               </span>
//             </div>

//             {/* Payment + NON PAYROLL: beneficiary fields */}
//             {!isPayroll && (
//               <>
//                 <div className="gx-bpr-sb-sep-one-trans-s-in-d-sec-fl">
//                   <span className="gx-bpr-sb-sep-one-trans-s-in-d-l">
//                     <p className="gx-p12-m">{t("beneficiary_name")}</p>
//                   </span>
//                   <span className="gx-bpr-sb-sep-one-trans-s-in-d-r">
//                     <p className="gx-p12-b">{paymentBeneName}</p>
//                   </span>
//                 </div>

//                 <div className="gx-bpr-sb-sep-one-trans-s-in-d-sec-fl">
//                   <span className="gx-bpr-sb-sep-one-trans-s-in-d-l">
//                     <p className="gx-p12-m">{t("bene_address")}</p>
//                   </span>
//                   <span className="gx-bpr-sb-sep-one-trans-cnt-rt gx-bpr-sb-sep-one-trans-s-in-d-r">
//                     <p className="gx-p12-b">{paymentBeneAddress}</p>
//                   </span>
//                 </div>

//                 <div className="gx-bpr-sb-sep-one-trans-s-in-d-sec-fl">
//                   <span className="gx-bpr-sb-sep-one-trans-s-in-d-l">
//                     <p className="gx-p12-m">{t("beneficiary_bank_name")}</p>
//                   </span>
//                   <span className="gx-bpr-sb-sep-one-trans-s-in-d-r">
//                     <p className="gx-p12-b">{paymentBeneBankName}</p>
//                   </span>
//                 </div>

//                 <div className="gx-bpr-sb-sep-one-trans-s-in-d-sec-fl">
//                   <span className="gx-bpr-sb-sep-one-trans-s-in-d-l">
//                     <p className="gx-p12-m">{t("beneficiary_bank_address")}</p>
//                   </span>
//                   <span className="gx-bpr-sb-sep-one-trans-cnt-rt gx-bpr-sb-sep-one-trans-s-in-d-r">
//                     <p className="gx-p12-b">{paymentBeneBankAddress}</p>
//                   </span>
//                 </div>
//               </>
//             )}

//             {isPayroll && (
//               <div className="gx-bpr-sb-sep-one-trans-s-in-d-sec-fl">
//                 <span className="gx-bpr-sb-sep-one-trans-s-in-d-l">
//                   <p className="gx-p12-m">{t("agent_code")}</p>
//                 </span>
//                 <span className="gx-bpr-sb-sep-one-trans-s-in-d-r">
//                   <p className="gx-p12-b">{payrollAgentId}</p>
//                 </span>
//               </div>
//             )}
//           </div>
//         )}
//       </div>

//       {/* === Transaction Details / Payroll Summary === */}
//       <div className="gx-bpr-sb-sep-one-trans-det-sec">
//         <button
//           type="button"
//           className="gx-bpr-sb-sep-one-trans-det-sec-fl"
//           onClick={() => toggleSection("transactionDetails")}
//         >
//           <h3 className="gx-p18-b">
//             {isPayroll ? "Payroll Summary" : "Transaction Details"}
//           </h3>
//           {expandedSections.transactionDetails ? (
//             <ChevronUp size={20} className="gx-bpr-sb-sep-one-trans-det-dd" />
//           ) : (
//             <ChevronDown size={20} className="gx-bpr-sb-sep-one-trans-det-dd" />
//           )}
//         </button>

//         {expandedSections.transactionDetails && (
//           <div className="gx-bpr-sb-sep-one-trans-det-in-data-sec">
//             {/* === Payment + NON PAYROLL: Transaction Details === */}
//             {!isPayroll && (
//               <>
//                 <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
//                   <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
//                     <p className="gx-p12-m">{t("payment_amount")}</p>
//                   </span>
//                   <span>
//                     <p className="gx-p12-b">{paymentAmount}</p>
//                   </span>
//                 </div>

//                 <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
//                   <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
//                     <p className="gx-p12-m">{t("fx_rate_indicative")}</p>
//                   </span>
//                   <span>
//                     <p className="gx-p12-b">{paymentForexRate}</p>
//                   </span>
//                 </div>

//                 <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
//                   <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
//                     <p className="gx-p12-m">{t("upi_transaction_id")}</p>
//                   </span>
//                   <span>
//                     <p className="gx-p12-b">{paymentCbsReferenceNo}</p>
//                   </span>
//                 </div>

//                 <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
//                   <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
//                     <p className="gx-p12-m">{t("purpose_of_payment")}</p>
//                   </span>
//                   <span>
//                     <p className="gx-p12-b">{paymentPurposeDesc}</p>
//                   </span>
//                 </div>

//                 <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
//                   <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
//                     <p className="gx-p12-m">{t("payment_details")}</p>
//                   </span>
//                   <span>
//                     <p className="gx-p12-b">{paymentDetails}</p>
//                   </span>
//                 </div>

//                 <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
//                   <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
//                     <p className="gx-p12-m">{t("payment_reference")}</p>
//                   </span>
//                   <span>
//                     <p className="gx-p12-b">{paymentReference}</p>
//                   </span>
//                 </div>

//                 <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
//                   <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
//                     <p className="gx-p12-m">{t("payment_category")}</p>
//                   </span>
//                   <span>
//                     <p className="gx-p12-b">{paymentCategory}</p>
//                   </span>
//                 </div>

//                 <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
//                   <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
//                     <p className="gx-p12-m">{t("charge_amount")}</p>
//                   </span>
//                   <span>
//                     <p className="gx-p12-b">{paymentChargeAmount}</p>
//                   </span>
//                 </div>

//                 <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
//                   <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
//                     <p className="gx-p12-m">{t("who_pays_charges")}</p>
//                   </span>
//                   <span>
//                     <p className="gx-p12-b">{paymentChargeBearerType}</p>
//                   </span>
//                 </div>

//                 <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
//                   <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
//                     <p className="gx-p12-m">{t("status")}</p>
//                   </span>
//                   <span>
//                     <StatusBadge
//                       module="bulk"
//                       statusCode={paymentRecordStatusCode}
//                     />
//                   </span>
//                 </div>
//               </>
//             )}

//             {isPayroll && (
//               <>
//                 <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
//                   <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
//                     <p className="gx-p12-m">{t("fixed_pay")}</p>
//                   </span>
//                   <span>
//                     <p className="gx-p12-b">{payrollFixedSalary}</p>
//                   </span>
//                 </div>

//                 <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
//                   <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
//                     <p className="gx-p12-m">{t("variable_pay")}</p>
//                   </span>
//                   <span>
//                     <p className="gx-p12-b">{payrollVariableSalary}</p>
//                   </span>
//                 </div>

//                 <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
//                   <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
//                     <p className="gx-p12-m">{t("housing_allowance")}</p>
//                   </span>
//                   <span>
//                     <p className="gx-p12-b">{payrollHousingAllowance}</p>
//                   </span>
//                 </div>

//                 <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
//                   <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
//                     <p className="gx-p12-m">{t("conveyance_allowance")}</p>
//                   </span>
//                   <span>
//                     <p className="gx-p12-b">{payrollConveyanceAllowance}</p>
//                   </span>
//                 </div>

//                 <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
//                   <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
//                     <p className="gx-p12-m">{t("medical_allowance")}</p>
//                   </span>
//                   <span>
//                     <p className="gx-p12-b">{payrollMedicalAllowance}</p>
//                   </span>
//                 </div>

//                 <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
//                   <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
//                     <p className="gx-p12-m">{t("annual_passage_allowance")}</p>
//                   </span>
//                   <span>
//                     <p className="gx-p12-b">{payrollAnnualPassageAllowance}</p>
//                   </span>
//                 </div>

//                 <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
//                   <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
//                     <p className="gx-p12-m">{t("overtime_allowance")}</p>
//                   </span>
//                   <span>
//                     <p className="gx-p12-b">{payrollOvertimeAllowance}</p>
//                   </span>
//                 </div>

//                 <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
//                   <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
//                     <p className="gx-p12-m">{t("all_other_allowances")}</p>
//                   </span>
//                   <span>
//                     <p className="gx-p12-b">{payrollOtherAllowance}</p>
//                   </span>
//                 </div>

//                 <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
//                   <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
//                     <p className="gx-p12-m">{t("leave_encashment")}</p>
//                   </span>
//                   <span>
//                     <p className="gx-p12-b">{payrollLeaveEncashment}</p>
//                   </span>
//                 </div>

//                 <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
//                   <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
//                     <p className="gx-p12-m">{t("status")}</p>
//                   </span>
//                   <span>
//                     <StatusBadge
//                       module="bulk"
//                       statusCode={payrollRecordStatusCode}
//                     />
//                   </span>
//                 </div>

//                 <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl gx-bup-payment-details">
//                   <h3 className="gx-p12-b">{t("payment_details")}</h3>
//                 </div>

//                 <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
//                   <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
//                     <p className="gx-p12-m">{t("bene_account_number_iban")}</p>
//                   </span>
//                   <span>
//                     <p className="gx-p12-b">
//                       {payrollEmpAccountNo ? (
//                         <MaskedAccountNumber
//                           accountNumber={payrollEmpAccountNo}
//                           showToggle
//                           toggleIconClassName="w-3 h-3 cursor-pointer opacity-70 hover:opacity-100"
//                         />
//                       ) : (
//                         <span className="gx-bpr-payroll-pd-in-cnt-red">
//                           {t("missing")}
//                         </span>
//                       )}
//                     </p>
//                   </span>
//                 </div>

//                 <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
//                   <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
//                     <p className="gx-p12-m">{t("agent_code")}</p>
//                   </span>
//                   <span>
//                     <p className="gx-p12-b">{payrollAgentId}</p>
//                   </span>
//                 </div>

//                 <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl gx-bup-payment-details">
//                   <h3 className="gx-p12-b">{t("duration_and_attendance")}</h3>
//                 </div>

//                 <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
//                   <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
//                     <p className="gx-p12-m">{t("start")}</p>
//                   </span>
//                   <span>
//                     <p className="gx-p12-b">{payrollPayStartDate}</p>
//                   </span>
//                 </div>

//                 <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
//                   <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
//                     <p className="gx-p12-m">{t("end")}</p>
//                   </span>
//                   <span>
//                     <p className="gx-p12-b">{payrollPayEndDate}</p>
//                   </span>
//                 </div>

//                 <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
//                   <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
//                     <p className="gx-p12-m">{t("days_worked")}</p>
//                   </span>
//                   <span>
//                     <p className="gx-p12-b">{payrollPayDuration}</p>
//                   </span>
//                 </div>

//                 <div className="gx-bpr-sb-sep-one-trans-det-in-d-sec-fl">
//                   <span className="gx-bpr-sb-sep-one-trans-det-in-d-l">
//                     <p className="gx-p12-m">{t("leave")}</p>
//                   </span>
//                   <span>
//                     <p className="gx-p12-b">{payrollLeaveDays}</p>
//                   </span>
//                 </div>
//               </>
//             )}
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default BulkUploadView;
